/**
 * The capture screen for one match — `/um/match/:tmsMatchDbId`.
 *
 * **The route key is the TMS DATABASE match id, never the match number.** `M07` is not
 * unique across the two competitions and a knockout renumbering would silently point this
 * screen at another match; binding to the database id is gate G-12 and is the same key the
 * note itself carries.
 *
 * The competition arrives as a query parameter because a note must carry one
 * (`note.competition_id` is NOT NULL) and this build has no fixture lookup on this screen.
 * It is validated here rather than trusted: anything that is not `1916` or `1917` leaves
 * the screen refusing to capture rather than writing a note into a competition that does
 * not exist.
 */

import { Link, useParams, useSearchParams } from "react-router-dom";

import { CapturePanel } from "../../features/um/CapturePanel.js";
import { HowToLink, HowToUse } from "../../app/HowToUse.js";
import { UM_ROOT_PATH } from "../../app/UmShell.js";
import { matchDebriefPath } from "./matchDebrief.js";

export function UmCapturePage(): React.JSX.Element {
  const { tmsMatchDbId } = useParams<{ tmsMatchDbId: string }>();
  const [search] = useSearchParams();

  const matchId = Number.parseInt(tmsMatchDbId ?? "", 10);
  const competition = search.get("competition");
  const competitionId =
    competition === "1916" || competition === "1917" ? competition : null;

  /**
   * THE WAY TO THE MATCH DEBRIEF — finding 1(a), run 73; gate removed by the Technical
   * Delegate's ruling of 2026-09-11 (run 73b).
   *
   * The control is ungated: the debrief screen is safe to open early — it says `No Released
   * Notes Yet For This Match` — and hiding the control before `matchHasLikelyEnded`'s guessed
   * full time left no way back into a debrief opened by link, and no way in at all before the
   * tournament. Today's finished-matches list keeps the time gate; this one match's own screen
   * does not.
   */
  return (
    <>
      <header className="umsect">
        {/* Back arrows name their destination, never a bare `‹` (gap J7). */}
        <Link className="umback" to={UM_ROOT_PATH}>
          ‹ Today
        </Link>
        <h2>Capture</h2>
        {/* Two taps from the app opening: Today → this match → Match Debrief. It sits in the header
            rather than at the foot of the note list, because at the final whistle it is the
            thing the manager is looking for and the list below it is long. */}
        {competitionId !== null && Number.isInteger(matchId) ? (
          <Link
            className="mdgo"
            data-testid="capture-debrief-link"
            to={matchDebriefPath(matchId, competitionId, "capture")}
          >
            Match Debrief
          </Link>
        ) : null}
        <HowToUse id="um-capture" title="How to use Capture">
          <li>
            Choose who the note is about in the <b>Umpire</b> box, or leave it as{" "}
            <b>Not About One Umpire</b> — a general note is a legitimate note.
          </li>
          <li>
            Type or dictate the note in{" "}
            <HowToLink to="capture-form">Type Or Dictate Your Note</HowToLink>. Describe what
            you saw, not what you conclude.
          </li>
          <li>
            <HowToLink to="capture-tags">Or Tap A Tag</HowToLink> takes about three seconds and
            lands the note against the right part of that umpire&rsquo;s marking later.
          </li>
          <li>
            <HowToLink to="capture-save">Save This Note</HowToLink> keeps it as a private draft.
            Nobody else can read it — not the other umpires managers and not the Technical
            Delegate.
          </li>
          {/*
           * RULING R4″ (Alfred, 2026-09-09), disclosed in the same run as the capability it
           * describes. The bullet above is still literally true — it is about the WORDS — and
           * this one says what the TD can now count, because leaving the reader to infer it
           * from the old promise is how a product ends up lying to the people it measures.
           */}
          <li>
            The Technical Delegate can see how many notes you have recorded, how many are
            pending and how many released, and when you last recorded &mdash; never what your
            drafts and locked notes say.
          </li>
          <li>
            <HowToLink to="my-notes">Your Notes On This Match</HowToLink> lists what you have
            written. <b>Lock</b> on a draft makes it yours for good.
          </li>
          <li>
            <b>Match Debrief</b> at the top of this screen opens the post-match debrief with
            this match&rsquo;s umpires. You can open it at any time; before full time it will
            simply have nothing released in it yet.
          </li>
          {/*
           * ~~"A released note is permanent: it cannot be withdrawn, unreleased or locked
           * again."~~ **Corrected 2026-09-11 (run 73).** That sentence stopped being true on
           * 9 September, when the Technical Delegate authorised the fifteen-minute
           * `Undo Release` (spec 11 §2's dated amendment), and a promise that has quietly
           * stopped being true is worse than no promise — it is the sentence a manager would
           * have trusted while a control beside her note said the opposite. What replaced it
           * says both true things: the window exists, and after it the note is permanent.
           */}
          <li>
            <HowToLink to="release-notes">Release To UM Team</HowToLink> shares the drafts you
            have ticked. For fifteen minutes after you press it you can take a release back with{" "}
            <b>Undo Release</b> on the note itself; after that the note is permanent and cannot
            be withdrawn, unreleased or locked again. A correction is a new note.
          </li>
        </HowToUse>
      </header>

      {!Number.isInteger(matchId) || competitionId === null ? (
        <section className="card">
          <p className="card__body">
            This link does not name a match and a competition, so nothing can be captured
            against it. Go back to Today and open the match from there.
          </p>
        </section>
      ) : (
        <CapturePanel matchId={matchId} competitionId={competitionId} />
      )}
    </>
  );
}
